import { getXPostHandle, getXPostId } from "@/x/post-url";

import {
  type CollectionAccentName,
  type CollectionLayoutName,
  type CreatorCollection,
  type CreatorCollectionMembership,
  type CreatorPost,
  type CreatorProfile,
  DEFAULT_COVER_CROP,
} from "./types";

interface CreatorPostInput {
  url: URL;
  title: string;
  context: string;
  profile: CreatorProfile;
}

interface CreatorCollectionInput {
  title: string;
  note: string;
  accent: CollectionAccentName;
  layout: CollectionLayoutName;
}

// Convert a validated X URL and optional framing into a reusable creator post.
//
export function createCreatorPost({
  url,
  title,
  context,
  profile,
}: CreatorPostInput): CreatorPost {
  const postHandle = getXPostHandle(url.toString()) ?? profile.handle;
  const isProfileHandle =
    normalizeCreatorHandle(postHandle) ===
    normalizeCreatorHandle(profile.handle);
  const authorHandle = `@${postHandle}`;

  return {
    id: createLocalId("post"),
    url: url.toString(),
    sourceKind: "x",
    sourceLabel: "X",
    authorName: isProfileHandle ? profile.name : authorHandle,
    authorHandle,
    title: title.trim() || "Linked X post",
    context: context.trim(),
    savedAt: new Date().toISOString(),
  };
}

// Create a private collection with a stable profile-relative slug.
//
export function createCreatorCollection({
  title,
  note,
  accent,
  layout,
}: CreatorCollectionInput): CreatorCollection {
  return {
    id: createLocalId("collection"),
    slug: slugifyTitle(title),
    title: title.trim(),
    note: note.trim(),
    accent,
    layout,
    isPublic: false,
    updatedAt: new Date().toISOString(),
    cover: "",
    coverSource: "",
    coverCrop: { ...DEFAULT_COVER_CROP },
    postIds: [],
    postMetadata: {},
  };
}

// Snapshot collection-local title and note when a reusable post is first added.
//
export function createMembershipMetadata({
  title,
  note,
}: {
  title: string;
  note: string;
}): CreatorCollectionMembership {
  return {
    title: title.trim() || "Linked post",
    note: note.trim(),
  };
}

// Accept only ordinary web links and normalize them before storage.
//
export function parseHttpUrl(value: string): URL | null {
  try {
    const parsedUrl = new URL(value.trim());
    return ["http:", "https:"].includes(parsedUrl.protocol) ? parsedUrl : null;
  } catch {
    return null;
  }
}

// Normalize handles for route matching and creator-link comparisons.
//
export function normalizeCreatorHandle(value: string): string {
  return value.trim().replace(/^@+/, "").toLowerCase();
}

// Produce readable, durable path segments for public creator collections.
//
export function slugifyTitle(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^\da-z]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return slug || `untitled-${Date.now()}`;
}

function createLocalId(prefix: string): string {
  const randomPart = Math.random().toString(36).slice(2, 9);
  return `${prefix}-${Date.now().toString(36)}-${randomPart}`;
}

export { getXPostHandle, getXPostId };
